/**
 * Leader Election — single-tab leader via BroadcastChannel.
 *
 * Guarantees exactly one leader tab per user across all same-origin tabs.
 * Used by the softphone (voice WS connects on leader only) and notification
 * systems (desktop alerts fire on leader only).
 *
 * Standalone module — no dependency on any WS client. Both the vanilla
 * (ws-client.ts) and React (WebSocketProvider) paths import this module
 * so the election runs regardless of which app shell is active, and Vite
 * cannot tree-shake it away.
 *
 * Call initLeaderElection() once at startup. Idempotent.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type NotifyMessage =
  | { kind: 'claim'; tabId: string; ts: number }
  | { kind: 'heartbeat'; tabId: string }
  | { kind: 'focus'; tabId: string; conversationId: string | null };

type LeaderCallback = () => void;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TAB_ID = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const HEARTBEAT_INTERVAL = 2_000;
const LEADER_TIMEOUT = 5_000;
const ELECTION_DELAY = 300;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let channel: BroadcastChannel | null = null;
let leader = false;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let watchdogTimer: ReturnType<typeof setTimeout> | null = null;
let electionTimer: ReturnType<typeof setTimeout> | null = null;
let lastHeartbeat = 0;
let initialized = false;

const becomeLeaderCallbacks: Set<LeaderCallback> = new Set();

/** Map of which tabs are focused on which conversations (leader only). */
const tabFocusMap = new Map<string, string | null>();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Start leader election. Call once at app startup. Idempotent.
 * After ~300ms (or ~5s if recovering from a stale leader), one tab
 * will be elected and all registered onBecomeLeader callbacks fire.
 */
export function initLeaderElection(): void {
  if (initialized) return;
  initialized = true;

  channel = new BroadcastChannel('apex-ws-notify');
  channel.onmessage = handleMessage;
  startElection();
}

/** Whether this tab is currently the leader. */
export function isLeader(): boolean {
  return leader;
}

/**
 * Register a callback that fires when this tab becomes leader.
 * If already leader, fires immediately (async, next microtask).
 * Returns an unsubscribe function.
 */
export function onBecomeLeader(cb: LeaderCallback): () => void {
  becomeLeaderCallbacks.add(cb);
  if (leader) {
    void Promise.resolve().then(() => {
      if (leader) cb();
    });
  }
  return () => becomeLeaderCallbacks.delete(cb);
}

/**
 * Report which conversation this tab currently has focused.
 * The leader uses this to suppress duplicate notifications
 * for conversations visible in any tab.
 */
export function setFocusedConversation(conversationId: string | null): void {
  tabFocusMap.set(TAB_ID, conversationId);
  channel?.postMessage({
    kind: 'focus',
    tabId: TAB_ID,
    conversationId,
  } satisfies NotifyMessage);
}

/** Check if a conversation is currently focused in any tab. */
export function isConversationFocused(conversationId: string): boolean {
  for (const cid of tabFocusMap.values()) {
    if (cid === conversationId) return true;
  }
  return false;
}

/** Tear down election. Call on app unmount. */
export function cleanup(): void {
  initialized = false;
  if (leader) relinquish();
  clearElectionTimer();
  clearWatchdog();
  if (channel) {
    channel.close();
    channel = null;
  }
}

// ---------------------------------------------------------------------------
// Election mechanics
// ---------------------------------------------------------------------------

function startElection(): void {
  clearElectionTimer();
  channel?.postMessage({
    kind: 'claim',
    tabId: TAB_ID,
    ts: Date.now(),
  } satisfies NotifyMessage);

  electionTimer = setTimeout(() => {
    electionTimer = null;
    if (!leader && Date.now() - lastHeartbeat > LEADER_TIMEOUT) {
      becomeLeader();
    }
  }, ELECTION_DELAY);
}

function becomeLeader(): void {
  if (leader) return;
  leader = true;
  console.info(`[Leader] Tab ${TAB_ID.slice(0, 8)} elected`);

  // Notify in-tab listeners
  window.dispatchEvent(new CustomEvent('apex:ws-leader'));
  for (const cb of becomeLeaderCallbacks) {
    try {
      cb();
    } catch (e) {
      console.error('[Leader] Callback error:', e);
    }
  }

  // Start heartbeat so followers know we're alive
  heartbeatTimer = setInterval(() => {
    channel?.postMessage({ kind: 'heartbeat', tabId: TAB_ID } satisfies NotifyMessage);
  }, HEARTBEAT_INTERVAL);

  // Send initial heartbeat immediately
  channel?.postMessage({ kind: 'heartbeat', tabId: TAB_ID } satisfies NotifyMessage);
}

function relinquish(): void {
  leader = false;
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

function startWatchdog(): void {
  clearWatchdog();
  watchdogTimer = setTimeout(() => {
    watchdogTimer = null;
    console.warn('[Leader] Heartbeat timeout — starting election');
    lastHeartbeat = 0;
    startElection();
  }, LEADER_TIMEOUT);
}

function clearWatchdog(): void {
  if (watchdogTimer) {
    clearTimeout(watchdogTimer);
    watchdogTimer = null;
  }
}

function clearElectionTimer(): void {
  if (electionTimer) {
    clearTimeout(electionTimer);
    electionTimer = null;
  }
}

// ---------------------------------------------------------------------------
// BroadcastChannel handler
// ---------------------------------------------------------------------------

function handleMessage(e: MessageEvent<NotifyMessage>): void {
  const msg = e.data;

  switch (msg.kind) {
    case 'claim': {
      if (leader) {
        channel?.postMessage({ kind: 'heartbeat', tabId: TAB_ID } satisfies NotifyMessage);
      }
      break;
    }

    case 'heartbeat': {
      lastHeartbeat = Date.now();
      if (!leader) {
        clearElectionTimer();
        startWatchdog();
      } else if (msg.tabId !== TAB_ID) {
        // Another leader exists — lower tabId wins
        if (msg.tabId < TAB_ID) {
          console.info(`[Leader] Yielding to ${msg.tabId.slice(0, 8)}`);
          relinquish();
          startWatchdog();
        }
      }
      break;
    }

    case 'focus': {
      tabFocusMap.set(msg.tabId, msg.conversationId);
      break;
    }
  }
}
